import api from './axiosConfig';

const MonedasService = {
  // ========================
  // DÓLAR HISTORIAL
  // ========================

  /**
   * Listar histórico de dólar
   * @param {Object} params - { from, to, limit } (from/to en formato YYYY-MM-DD)
   */
  listarDolar: async (params = {}) => {
    const res = await api.get('/monedas/dolar', { params });
    return res.data;
  },

  /**
   * Obtener dólar para una fecha específica, resuelto con la fuente de cotización
   * de la empresa (Bluelytics / DolarApi). Sin empresaId, Bluelytics.
   * @param {string} fecha - formato YYYY-MM-DD
   * @param {string} [empresaId]
   */
  obtenerDolar: async (fecha, empresaId = null) => {
    const res = await api.get(`/monedas/dolar/${encodeURIComponent(fecha)}`, {
      params: empresaId ? { empresaId } : {},
    });
    return res.data;
  },

  /**
   * Crear/actualizar valores de dólar para una fecha
   * @param {Object} data - { fecha, oficial, blue, mep }
   */
  crearDolar: async (data) => {
    const res = await api.post('/monedas/dolar', data);
    return res.data;
  },

  /**
   * Actualizar valores de dólar para una fecha
   * @param {string} fecha - formato YYYY-MM-DD
   * @param {Object} data - { oficial, blue, mep }
   */
  actualizarDolar: async (fecha, data) => {
    const res = await api.put(`/monedas/dolar/${encodeURIComponent(fecha)}`, data);
    return res.data;
  },

  /**
   * Eliminar registro de dólar
   * @param {string} fecha - formato YYYY-MM-DD
   */
  eliminarDolar: async (fecha) => {
    const res = await api.delete(`/monedas/dolar/${encodeURIComponent(fecha)}`);
    return res.data;
  },

  /**
   * Tipo de cambio efectivo que tomaría un movimiento de la empresa en esa fecha
   * (vigente de la empresa, si no el histórico). Es el mismo que aplica el alta
   * real, así una previsualización no se despega del movimiento que se va a crear.
   * @param {string} empresaId
   * @param {string} [fecha] - formato YYYY-MM-DD; default: hoy en el backend
   * @returns {Promise<number|null>} valor, o null si no hay cotización
   */
  obtenerDolarReferencia: async (empresaId, fecha = null) => {
    const res = await api.get(`/monedas/dolar-referencia/${encodeURIComponent(empresaId)}`, {
      params: fecha ? { fecha } : {},
    });
    return res.data?.valor ?? null;
  },

  /**
   * Blue venta de la fecha (hoy si no se pasa) con la fuente de cotización de la
   * empresa; sin empresaId, Bluelytics.
   */
  obtenerDolarBlueVenta: async (fecha = null, empresaId = null) => {
    const res = await api.get('/monedas/dolar-blue-venta', {
      params: { ...(fecha ? { fecha } : {}), ...(empresaId ? { empresaId } : {}) },
    });
    return res.data?.valor ?? null;
  },

  // ========================
  // CAC INDICES
  // ========================

  /**
   * Listar histórico de índices CAC
   * @param {Object} params - { from, to, limit } (from/to en formato YYYY-MM)
   */
  listarCAC: async (params = {}) => {
    const res = await api.get('/monedas/cac', { params });
    return res.data;
  },

  /**
   * Obtener índice CAC para un mes específico
   * @param {string} fecha - formato YYYY-MM
   */
  obtenerCAC: async (fecha) => {
    const res = await api.get(`/monedas/cac/${encodeURIComponent(fecha)}`);
    return res.data;
  },

  /**
   * Resolver las 3 variantes del índice CAC (legacy / estimado / automatico) para un mes.
   * Devuelve { fecha_objetivo, pendiente, legacy, estimado, automatico }; cada variante trae
   * { general, mano_obra, materiales, fecha_utilizada, disponible } (+ detalle en estimado proyectado).
   * @param {string} fecha - formato YYYY-MM
   */
  obtenerVariantesCAC: async (fecha) => {
    const res = await api.get(`/monedas/cac/variantes/${encodeURIComponent(fecha)}`);
    return res.data;
  },

  /**
   * Crear/actualizar índice CAC para un mes
   * @param {Object} data - { fecha, general, materiales, mano_obra }
   */
  crearCAC: async (data) => {
    const res = await api.post('/monedas/cac', data);
    return res.data;
  },

  /**
   * Actualizar índice CAC para un mes
   * @param {string} fecha - formato YYYY-MM
   * @param {Object} data - { general, materiales, mano_obra }
   */
  actualizarCAC: async (fecha, data) => {
    const res = await api.put(`/monedas/cac/${encodeURIComponent(fecha)}`, data);
    return res.data;
  },

  /**
   * Eliminar índice CAC
   * @param {string} fecha - formato YYYY-MM
   */
  eliminarCAC: async (fecha) => {
    const res = await api.delete(`/monedas/cac/${encodeURIComponent(fecha)}`);
    return res.data;
  },

  // ========================
  // IPC INDICES
  // ========================

  /**
   * Listar histórico de índices IPC
   * @param {Object} params - { from, to, limit } (from/to en formato YYYY-MM)
   */
  listarIPC: async (params = {}) => {
    const res = await api.get('/monedas/ipc', { params });
    return res.data;
  },

  /**
   * Obtener índice IPC para un mes específico
   * @param {string} fecha - formato YYYY-MM
   */
  obtenerIPC: async (fecha) => {
    const res = await api.get(`/monedas/ipc/${encodeURIComponent(fecha)}`);
    return res.data;
  },

  /**
   * Crear/actualizar índice IPC para un mes
   * @param {Object} data - { fecha, valor }
   */
  crearIPC: async (data) => {
    const res = await api.post('/monedas/ipc', data);
    return res.data;
  },

  /**
   * Actualizar índice IPC para un mes
   * @param {string} fecha - formato YYYY-MM
   * @param {Object} data - { valor }
   */
  actualizarIPC: async (fecha, data) => {
    const res = await api.put(`/monedas/ipc/${encodeURIComponent(fecha)}`, data);
    return res.data;
  },

  /**
   * Eliminar índice IPC
   * @param {string} fecha - formato YYYY-MM
   */
  eliminarIPC: async (fecha) => {
    const res = await api.delete(`/monedas/ipc/${encodeURIComponent(fecha)}`);
    return res.data;
  },

  /**
   * Sincronizar la serie de IPC contra la publicada por INDEC.
   * @returns {Object} { nuevos, actualizados, sinCambios, desde, hasta }
   */
  importarIPC: async () => {
    const res = await api.post('/monedas/ipc/importar');
    return res.data;
  },
};

export default MonedasService;
